import { Directive, EventEmitter, HostListener, Input, Output, } from '@angular/core';
import { combineLatest } from 'rxjs';
import { delay, filter, tap } from 'rxjs/operators';
import { DragAndDropService } from './drag-and-drop.service';
import * as i0 from "@angular/core";
import * as i1 from "./drag-and-drop.service";
export class DroppableDirective {
    constructor(elementRef, renderer, droppableDraggableService) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.droppableDraggableService = droppableDraggableService;
        this.scrollDirection = 'horizontal';
        this.dragStart = new EventEmitter();
        this.dragging = new EventEmitter();
        this.dragEnd = new EventEmitter();
        this.subscriptions = [];
        // CMAP-3946: Ensure changes to draggable array are complete before handling drag start
        const dragStart$ = combineLatest([
            droppableDraggableService.isDraggableCurrent$,
            droppableDraggableService.onDragStart$.pipe(delay(1), // Delay emit 1ms to allow draggable changes to kick off
            tap(() => (this.isWaitingToStartDrag = true))),
        ]).pipe(filter(([isDraggableCurrent]) => isDraggableCurrent && this.isWaitingToStartDrag));
        this.subscriptions = [
            dragStart$.subscribe(this.handleDragStart.bind(this)),
            droppableDraggableService.onDragEnd$.subscribe((dragEnd) => {
                this.dragEnd.emit(dragEnd);
            }),
            droppableDraggableService.onDragging$.subscribe((dragging) => {
                this.dragging.emit(dragging);
                this.checkImpact(dragging.currDraggable);
            }),
            droppableDraggableService.draggableChildren$.subscribe((draggables) => (this.draggables = draggables)),
        ];
    }
    handleBodyMouseLeave() {
        this.handleMouseUp();
    }
    handleMouseUp() {
        this.isWaitingToStartDrag = false;
        if (!this.isDragging) {
            return true;
        }
        this.draggables.forEach((draggable) => draggable.resetTransform());
        this.isDragging = false;
        return undefined;
    }
    handleScroll(event) {
        if (!this.isDragging) {
            return true;
        }
        this.droppableDraggableService.emitParentScroll(event);
        return undefined;
    }
    handleDragStart(params) {
        const [, data] = params;
        this.isDragging = true;
        this.shiftedDraggables = [...this.draggables];
        this.impactors = [];
        const draggableElement = this.shiftedDraggables[data.startIndex].elementRef.nativeElement;
        const transformDistances = this.getTransformDistances(draggableElement);
        this.draggables.forEach((draggable, index) => {
            draggable.setTransformVector(transformDistances);
            if (index < data.startIndex) {
                draggable.setShiftMinMax(0, 1);
            }
            else if (index > data.startIndex) {
                draggable.setShiftMinMax(-1, 0);
            }
            else {
                draggable.setShiftMinMax(index * -1, this.draggables.length - index);
            }
            if (index !== data.startIndex) {
                this.renderer.setStyle(draggable.elementRef.nativeElement, 'transition', 'transform .1s');
            }
        });
        this.isWaitingToStartDrag = false;
    }
    getTransformDistances(el) {
        const rect = el.getBoundingClientRect();
        if (this.scrollDirection === 'horizontal') {
            return { x: rect.width, y: 0 };
        }
        return { x: 0, y: rect.height };
    }
    checkImpact(currDraggable) {
        // TODO: Improve this. See CMAP-4025.
        if (!this.isDragging) {
            return;
        }
        // check for collision with the neighbor to the immediate left and right.
        let movingLeft = false;
        let movingRight = false;
        if (currDraggable.getShiftIndex() > 0) {
            movingLeft = this.didImpactNeighbor(currDraggable, this.shiftedDraggables[currDraggable.getShiftIndex() - 1]);
        }
        if (currDraggable.getShiftIndex() < this.draggables.length - 1) {
            movingRight = this.didImpactNeighbor(currDraggable, this.shiftedDraggables[currDraggable.getShiftIndex() + 1]);
        }
    }
    didImpactNeighbor(currDraggable, neighborDraggable) {
        if (this.impactors.indexOf(neighborDraggable) >= 0) {
            return false;
        }
        const currDraggableElement = currDraggable.elementRef.nativeElement;
        const boundingRect = currDraggableElement.getBoundingClientRect();
        const neighborDraggableElement = neighborDraggable.elementRef.nativeElement;
        const neighborRect = neighborDraggableElement.getBoundingClientRect();
        let midPoint;
        // which side of the neighbors to check when determining if the dragged node should upshift or downshift.
        let downShiftBorder;
        let upShiftBorder;
        if (this.scrollDirection === 'horizontal') {
            const { left, width } = boundingRect;
            const l = Number(left);
            const w = Number(width);
            midPoint = l + w / 2;
            downShiftBorder = 'right';
            upShiftBorder = 'left';
        }
        else {
            const { top, height } = boundingRect;
            const t = Number(top);
            const h = Number(height);
            midPoint = t + h / 2;
            downShiftBorder = 'bottom';
            upShiftBorder = 'top';
        }
        let midPointColliding;
        let downShifter;
        let upShifter;
        if (currDraggable.getShiftIndex() > neighborDraggable.getShiftIndex()) {
            midPointColliding = midPoint < neighborRect[downShiftBorder];
            upShifter = neighborDraggable;
            downShifter = currDraggable;
        }
        else {
            midPointColliding = midPoint > neighborRect[upShiftBorder];
            upShifter = currDraggable;
            downShifter = neighborDraggable;
        }
        if (midPointColliding) {
            this.shiftedDraggables[upShifter.getShiftIndex()] = downShifter;
            this.shiftedDraggables[downShifter.getShiftIndex()] = upShifter;
            upShifter.shiftUp();
            downShifter.shiftDown();
            this.impactors.push(neighborDraggable);
            setTimeout(() => {
                this.impactors.splice(this.impactors.indexOf(neighborDraggable), 1);
            }, 110);
            return true;
        }
        return false;
    }
    ngOnInit() {
        this.droppableDraggableService.setParentContainer(this);
    }
    ngOnDestroy() {
        // delete all subscriptions
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }
}
DroppableDirective.ɵfac = function DroppableDirective_Factory(t) { return new (t || DroppableDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i1.DragAndDropService)); };
DroppableDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: DroppableDirective, selectors: [["", "mheDroppable", ""]], hostBindings: function DroppableDirective_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("mouseleave", function DroppableDirective_mouseleave_HostBindingHandler() { return ctx.handleBodyMouseLeave(); }, false, i0.ɵɵresolveBody)("mouseup", function DroppableDirective_mouseup_HostBindingHandler() { return ctx.handleMouseUp(); }, false, i0.ɵɵresolveWindow)("scroll", function DroppableDirective_scroll_HostBindingHandler($event) { return ctx.handleScroll($event); });
    } }, inputs: { scrollDirection: "scrollDirection" }, outputs: { dragStart: "dragStart", dragging: "dragging", dragEnd: "dragEnd" }, features: [i0.ɵɵProvidersFeature([DragAndDropService])] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DroppableDirective, [{
        type: Directive,
        args: [{
                selector: '[mheDroppable]',
                providers: [DragAndDropService],
            }]
    }], function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1.DragAndDropService }]; }, { scrollDirection: [{
            type: Input
        }], dragStart: [{
            type: Output
        }], dragging: [{
            type: Output
        }], dragEnd: [{
            type: Output
        }], handleBodyMouseLeave: [{
            type: HostListener,
            args: ['body:mouseleave']
        }], handleMouseUp: [{
            type: HostListener,
            args: ['window:mouseup']
        }], handleScroll: [{
            type: HostListener,
            args: ['scroll', ['$event']]
        }] }); })();
